import React, { useEffect, useState, useSyncExternalStore } from 'react'
import { Button, Result, Spin } from 'antd'
import { getAuthorizationVersion, getProfile } from '@/api/login'
import { getAuthSnapshot, getToken, getUser, setUser, subscribeAuth } from '@/utils/auth'

export const AUTHORIZATION_POLL_MS = 5000

// A version change also invalidates already displayed data and open dialogs.
// Polling is background traffic and must never extend the user's idle session.
const AuthorizationBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useSyncExternalStore(subscribeAuth, getAuthSnapshot)
  const token = getToken()
  const [state, setState] = useState({ token: '', ready: false, failed: false, revision: 0 })
  const [retry, setRetry] = useState(0)
  useEffect(() => {
    if (!token) return
    let cancelled = false
    let running = false
    let pending = false
    let version: string | undefined
    let signature: string | undefined
    const current = () => !cancelled && getToken() === token
    const sync = async (force = false) => {
      if (!current()) return
      if (running) { pending = pending || force; return }
      running = true
      try {
        const result = await getAuthorizationVersion()
        if (!current()) return
        if (!result.success || !result.result) throw new Error('version unavailable')
        const nextVersion = result.result.version
        if (force || nextVersion !== version) {
          const profile = await getProfile()
          if (!current()) return
          if (!profile.success || !profile.result) throw new Error('profile unavailable')
          const saved = getUser()
          const nextSignature = JSON.stringify([profile.result.authorization_revision || nextVersion, profile.result.permissions, profile.result.role, profile.result.tenant_id])
          const changed = signature !== nextSignature
          // Compare complete storage to avoid unnecessary auth subscriptions.
          const nextState = { ...saved, user: { ...saved?.user, ...profile.result } }
          if (JSON.stringify(nextState) !== getAuthSnapshot()) setUser(nextState, false)
          signature = nextSignature
          version = nextVersion
          setState(old => ({ token, ready: true, failed: false, revision: old.revision + (changed ? 1 : 0) }))
        } else {
          setState(old => ({ ...old, token, ready: true, failed: false }))
        }
      } catch {
        if (current()) {
          // Hide old rows on failed identity/tenant checks, and retry on recovery.
          setState(old => ({ ...old, token, ready: false, failed: true }))
          version = undefined
        }
      } finally {
        running = false
        if (pending && current()) { pending = false; sync(true) }
      }
    }
    const refresh = () => {
      if (document.visibilityState !== 'hidden') {
        // Revalidate in the background. Only a changed grant or failed check
        // invalidates the mounted page; focus alone must preserve tabs/forms.
        sync(true)
      }
    }
    sync(true)
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') sync()
    }, AUTHORIZATION_POLL_MS)
    window.addEventListener('focus', refresh)
    window.addEventListener('online', refresh)
    window.addEventListener('authorization-denied', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      cancelled = true
      window.clearInterval(timer)
      window.removeEventListener('focus', refresh)
      window.removeEventListener('online', refresh)
      window.removeEventListener('authorization-denied', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [token, retry])
  if (!token) return null
  if (state.token !== token || !state.ready) {
    return state.failed && state.token === token
      ? <Result status="warning" title="暂时无法确认访问权限" subTitle="请检查网络，或联系管理员确认账号和组织状态。" extra={<Button onClick={() => setRetry(value => value + 1)}>重新检查</Button>} />
      : <div style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}><Spin tip="正在确认访问权限" /></div>
  }
  return <React.Fragment key={state.revision}>{children}</React.Fragment>
}
export default AuthorizationBoundary
